import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaLock, FaUserCheck, FaEye } from 'react-icons/fa';

const Privacy = () => {
  const lastUpdated = "September 25, 2025";

  const sections = [
    {
      id: "information-collection",
      title: "Information We Collect",
      icon: <FaUserCheck className="h-6 w-6" />,
      content: [
        {
          subtitle: "Personal Information",
          text: "When you use Boulevard, we collect personal information that you voluntarily provide, including your name, email address, phone number, and location data necessary for ride matching."
        },
        {
          subtitle: "Usage Data",
          text: "We automatically collect information about your interactions with our service, including ride history, preferences, and performance metrics to improve our matching algorithm."
        },
        {
          subtitle: "Device Information",
          text: "We may collect information about your device, including device type, operating system, and unique device identifiers to ensure service compatibility and security."
        }
      ]
    },
    {
      id: "information-use",
      title: "How We Use Your Information",
      icon: <FaEye className="h-6 w-6" />,
      content: [
        {
          subtitle: "Service Provision",
          text: "We use your information to match you with compatible drivers or riders, facilitate communication, and process payments securely."
        },
        {
          subtitle: "Service Improvement",
          text: "Your data helps us improve our algorithms, develop new features, and enhance the overall user experience on our platform."
        },
        {
          subtitle: "Communication",
          text: "We use your contact information to send you service-related notifications, updates, and respond to your inquiries."
        },
        {
          subtitle: "Safety and Security",
          text: "We process your information to verify user identities, prevent fraud, and ensure the safety of all platform users."
        },
        {
          subtitle: "Community Service Operation",
          text: "As a community service by iautenticai company, we do not use your data for advertising, selling to third parties, or profit generation. Your information is used solely to provide the ride-sharing service."
        }
      ]
    },
    {
      id: "information-sharing",
      title: "Information Sharing",
      icon: <FaLock className="h-6 w-6" />,
      content: [
        {
          subtitle: "With Other Users",
          text: "We share necessary information (name, profile picture, ratings) with matched users to facilitate ride coordination. Full contact details are only shared after mutual consent."
        },
        {
          subtitle: "Service Providers",
          text: "We may share information with trusted third-party service providers who help us operate our platform, including payment processors and cloud hosting services."
        },
        {
          subtitle: "Legal Compliance",
          text: "We only disclose your information to law enforcement or legal authorities when required by valid legal process (such as a court order or warrant) in cases where a crime has been committed or when someone is accused of a crime. We do not share your data for general law enforcement requests or policy enforcement unless criminal activity is involved."
        },
        {
          subtitle: "Business Transfers",
          text: "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction."
        }
      ]
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: <FaShieldAlt className="h-6 w-6" />,
      content: [
        {
          subtitle: "Encryption",
          text: "All data transmission is protected using industry-standard SSL/TLS encryption. Your personal information is encrypted at rest using AES-256 encryption."
        },
        {
          subtitle: "Access Controls",
          text: "We implement strict access controls to ensure that only authorized personnel can access your personal information, and only when necessary for service provision."
        },
        {
          subtitle: "Regular Audits",
          text: "We conduct regular security audits and vulnerability assessments to identify and address potential security risks."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your personal information only as long as necessary to provide our services or as required by law. You can request data deletion at any time."
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Boulevard</title>
        <meta name="description" content="Boulevard Privacy Policy - Learn how we collect, use, and protect your personal information in our community ride-sharing service." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Your privacy is important to us. This policy explains how we collect, 
              use, and protect your personal information.
            </p>
            <p className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
              <h2 className="text-2xl font-semibold text-blue-800 mb-3">
                Our Commitment to Privacy
              </h2>
              <p className="text-blue-700 mb-0">
                At Boulevard, we are committed to protecting your privacy and maintaining 
                the confidentiality of your personal information. As a community service 
                by iautenticai, this Privacy Policy describes how we collect, use, 
                disclose, and safeguard your information when you use our community 
                ride-sharing platform and Telegram bot services.
              </p>
            </div>

            <p className="text-gray-600 text-lg leading-relaxed">
              By using Boulevard, you consent to the collection and use of your information 
              in accordance with this Privacy Policy. If you do not agree with the terms 
              of this policy, please do not use our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="text-primary-600">
                    {section.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Your Privacy Rights
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Access Your Data",
                  description: "You have the right to request a copy of the personal information we hold about you."
                },
                {
                  title: "Correct Your Data",
                  description: "You can request that we correct any inaccurate or incomplete personal information."
                },
                {
                  title: "Delete Your Data",
                  description: "You can request that we delete your personal information, subject to certain exceptions."
                },
                {
                  title: "Data Portability",
                  description: "You can request a copy of your data in a structured, machine-readable format."
                },
                {
                  title: "Withdraw Consent",
                  description: "You can withdraw your consent for data processing at any time."
                },
                {
                  title: "Object to Processing",
                  description: "You can object to certain types of data processing, including marketing communications."
                }
              ].map((right, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {right.title}
                  </h3>
                  <p className="text-gray-600">
                    {right.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">
                To exercise any of these rights, please contact us using the information below.
              </p>
              <a
                href="/contact"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Contact Privacy Team</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cookies and Tracking */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Cookies and Tracking Technologies
            </h2>

            <div className="space-y-4">
              <p className="text-gray-600">
                We use cookies and similar tracking technologies to enhance your experience 
                on our website and mobile applications. These technologies help us:
              </p>

              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze website usage and performance</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure security and prevent fraud</li>
              </ul>

              <p className="text-gray-600">
                You can control cookie settings through your browser preferences. However, 
                disabling certain cookies may affect the functionality of our services.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* International Data Transfers */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              International Data Transfers
            </h2>

            <div className="space-y-4">
              <p className="text-gray-600">
                Your information may be transferred to and processed in countries other than 
                your country of residence. These countries may have data protection laws that 
                are different from the laws of your country.
              </p>

              <p className="text-gray-600">
                When we transfer your personal information internationally, we implement 
                appropriate safeguards to protect your information, including:
              </p>

              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Standard contractual clauses approved by relevant authorities</li>
                <li>Adequacy decisions from competent data protection authorities</li>
                <li>Certification under approved frameworks</li>
                <li>Additional technical and organizational security measures</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6">
              Questions About This Policy?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              If you have any questions about this Privacy Policy or our data practices, 
              please don't hesitate to contact us.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              <div>
                <h3 className="font-semibold mb-2">Email Us</h3>
                <p className="text-primary-100">privacy@rideease.app</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Mail Us</h3>
                <p className="text-primary-100">
                  RideEase Privacy Team<br />
                  123 Innovation Street<br />
                  Tech City, TC 12345
                </p>
              </div>
            </div>

            <div className="mt-8">
              <a
                href="/contact"
                className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors inline-flex items-center space-x-2"
              >
                <span>Contact Us</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Updates Notice */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Policy Updates
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We may update this Privacy Policy from time to time. We will notify you 
              of any changes by posting the new Privacy Policy on this page and updating 
              the "Last updated" date. We encourage you to review this Privacy Policy 
              periodically for any changes.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Privacy;
